import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, Search } from "lucide-react";
import { useCurrency } from "@/hooks/useUserCurrency";

let fmtBRL: (v: any) => string = (v: any) => {
  const n = Number(v);
  if (!isFinite(n)) return String(v ?? "—");
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

type AuditRow = {
  id: string;
  user_id: string;
  loan_id: string | null;
  client_name: string | null;
  action: "created" | "updated" | "deleted" | "client_deleted";
  old_data: any;
  new_data: any;
  changed_fields: string[] | null;
  created_at: string;
};

type PaymentRow = {
  loan_id: string;
  amount: number | null;
  interest_amount: number | null;
  principal_amount: number | null;
  is_settlement: boolean | null;
  created_at: string;
};

type PaymentInfo =
  | { kind: "settlement"; value: number }
  | { kind: "interest"; value: number }
  | { kind: "interest_zero" }
  | { kind: "installment"; value: number };

const FIELD_LABELS: Record<string, string> = {
  amount: "Valor",
  due_date: "Vencimento",
  loan_date: "Data do empréstimo",
  interest_rate: "Taxa de juros",
  interest_type: "Tipo de juros",
  status: "Status",
  installments: "Parcelas",
  paid_installments: "Parcelas pagas",
  daily_penalty: "Multa diária",
  installment_frequency: "Frequência",
  notes: "Observações",
  settled_at: "Encerrado em",
  client_id: "Cliente",
};

const HIDDEN_FIELDS = new Set(["updated_at", "created_at", "id", "user_id", "cycle_days"]);

const VALUE_LABELS: Record<string, Record<string, string>> = {
  status: {
    active: "Ativo",
    paid: "Quitado",
    settled: "Quitado",
    quitado: "Quitado",
    overdue: "Em atraso",
    pending: "Pendente",
    renegotiated: "Renegociado",
  },
  interest_type: {
    fixed: "Fixo",
    compound: "Composto",
    simple: "Simples",
    monthly: "Mensal",
    daily: "Diário",
  },
  installment_frequency: {
    monthly: "Mensal",
    weekly: "Semanal",
    biweekly: "Quinzenal",
    daily: "Diário",
  },
};


const fmtTimeBR = (s: string) => {
  try {
    return new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
};

const parseDateOnly = (v: any): Date | null => {
  if (!v) return null;
  const [y, m, d] = String(v).split("T")[0].split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dayGroupLabel = (s: string) => {
  const d = new Date(s);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Hoje";
  if (sameDay(d, yest)) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtValue = (field: string, value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (VALUE_LABELS[field] && VALUE_LABELS[field][String(value)]) {
    return VALUE_LABELS[field][String(value)];
  }
  if (field === "amount" || field === "daily_penalty") return fmtBRL(value);
  if (field === "interest_rate") return `${value}%`;
  if (field === "due_date" || field === "loan_date") {
    const [y, m, d] = String(value).split("T")[0].split("-");
    return d && m && y ? `${d}/${m}/${y}` : String(value);
  }
  if (field === "settled_at") {
    try {
      return new Date(value).toLocaleString("pt-BR");
    } catch {
      return String(value);
    }
  }
  if (field === "notes") {
    const s = String(value);
    return s.length > 60 ? s.slice(0, 60) + "…" : s;
  }
  if (field === "client_id") {
    return "Cliente alterado";
  }
  return String(value);
};

const deltaFor = (field: string, oldV: any, newV: any): { text: string; tone: "up" | "down" | "neutral" } | null => {
  if (field === "due_date" || field === "loan_date") {
    const a = parseDateOnly(oldV);
    const b = parseDateOnly(newV);
    if (!a || !b) return null;
    const diff = Math.round((b.getTime() - a.getTime()) / 86400000);
    if (diff === 0) return null;
    const sign = diff > 0 ? "+" : "";
    return { text: `(${sign}${diff} dias)`, tone: diff > 0 ? "up" : "down" };
  }
  if (field === "amount" || field === "daily_penalty") {
    const a = Number(oldV); const b = Number(newV);
    if (!isFinite(a) || !isFinite(b)) return null;
    const diff = b - a;
    if (diff === 0) return null;
    const sign = diff > 0 ? "+" : "-";
    return { text: `(${sign}${fmtBRL(Math.abs(diff))})`, tone: diff > 0 ? "up" : "down" };
  }
  return null;
};

const actionBadge = (action: AuditRow["action"]) => {
  if (action === "created")
    return (
      <Badge className="bg-success/15 text-success border-success/25 hover:bg-success/20">
        Criado
      </Badge>
    );
  if (action === "updated")
    return (
      <Badge className="bg-primary/15 text-primary border-primary/25 hover:bg-primary/20">
        Alterado
      </Badge>
    );
  if (action === "client_deleted")
    return (
      <Badge className="bg-destructive/25 text-destructive border-destructive/40 hover:bg-destructive/30 font-semibold">
        Cliente excluído
      </Badge>
    );
  return (
    <Badge className="bg-destructive/15 text-destructive border-destructive/25 hover:bg-destructive/20">
      Excluído
    </Badge>
  );
};

const isQuitadoStatus = (v: any) =>
  v === "paid" || v === "settled" || v === "quitado";

const eventTitle = (row: AuditRow): string => {
  if (row.action === "created") return "Empréstimo criado";
  if (row.action === "deleted") return "Empréstimo excluído";
  if (row.action === "client_deleted") return "Cliente excluído";
  const fields = (row.changed_fields ?? []).filter((f) => !HIDDEN_FIELDS.has(f));
  const set = new Set(fields);
  if (set.has("status") && isQuitadoStatus(row.new_data?.status)) {
    return "Empréstimo quitado";
  }
  if (set.has("due_date") && set.has("paid_installments")) return "Parcela paga";
  if (fields.length === 1 && set.has("due_date")) return "Vencimento prorrogado";
  if (set.has("amount")) return "Valor alterado";
  return "Empréstimo alterado";
};

const renderSummary = (row: AuditRow) => {
  if (row.action === "created") {
    const amt = row.new_data?.amount;
    return <span>Empréstimo criado: {fmtBRL(amt)}</span>;
  }
  if (row.action === "client_deleted") {
    const count = row.old_data?.loans_count ?? 0;
    const total = row.old_data?.loans_total ?? 0;
    return (
      <span>
        {count} empréstimo{count === 1 ? "" : "s"} apagado{count === 1 ? "" : "s"} · Total: {fmtBRL(total)}
      </span>
    );
  }
  if (row.action === "deleted") {
    const amt = row.old_data?.amount;
    const due = row.old_data?.due_date;
    return (
      <span>
        Empréstimo excluído: {fmtBRL(amt)}
        {due ? `, vencimento ${fmtValue("due_date", due)}` : ""}
      </span>
    );
  }
  let fields = (row.changed_fields ?? []).filter((f) => !HIDDEN_FIELDS.has(f));
  if (fields.length === 0) return <span className="text-muted-foreground">Sem alterações relevantes</span>;

  const title = eventTitle(row);

  // Quitação: mostrar só o título (omitir status/settled_at redundantes)
  if (title === "Empréstimo quitado") {
    return null;
  }

  // Parcela paga: resumo dedicado
  if (title === "Parcela paga") {
    const paid = row.new_data?.paid_installments;
    const total = row.new_data?.installments;
    const next = row.new_data?.due_date ? fmtValue("due_date", row.new_data.due_date) : "—";
    return <span>Parcela {paid}/{total} · próximo vencimento {next}</span>;
  }

  // Vencimento prorrogado
  if (title === "Vencimento prorrogado") {
    const oldV = fmtValue("due_date", row.old_data?.due_date);
    const newV = fmtValue("due_date", row.new_data?.due_date);
    const d = deltaFor("due_date", row.old_data?.due_date, row.new_data?.due_date);
    return (
      <span>
        {oldV} <span className="text-muted-foreground">→</span> {newV}{" "}
        {d && (
          <span className={d.tone === "up" ? "text-success" : "text-destructive"}>{d.text}</span>
        )}
      </span>
    );
  }

  return (
    <ul className="space-y-0.5">
      {fields.map((f) => {
        const label = FIELD_LABELS[f] ?? f;
        const oldV = fmtValue(f, row.old_data?.[f]);
        const newV = fmtValue(f, row.new_data?.[f]);
        const d = deltaFor(f, row.old_data?.[f], row.new_data?.[f]);
        return (
          <li key={f} className="text-sm">
            <span className="font-medium">{label}:</span>{" "}
            <span className="text-muted-foreground">{oldV}</span>{" "}
            <span className="text-muted-foreground">→</span>{" "}
            <span className="text-foreground">{newV}</span>
            {d && (
              <span className={"ml-1 " + (d.tone === "up" ? "text-success" : "text-destructive")}>
                {d.text}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

interface Props {
  userId: string;
}

type FilterKind = "all" | "created" | "updated" | "paid" | "deleted" | "interest_only" | "paid_settlement";

export const LoanAuditLog = ({ userId }: Props) => {
  const { format: _fmtCur } = useCurrency();
  fmtBRL = (v: any) => {
    const n = Number(v);
    if (!isFinite(n)) return String(v ?? "—");
    return _fmtCur(n);
  };
  const [limit, setLimit] = useState(300);
  const [filter, setFilter] = useState<FilterKind>("all");
  const [search, setSearch] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["loan-audit-log", userId, limit],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loan_audit_log" as any)
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as AuditRow[];
    },
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["loan-audit-payments", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loan_payments" as any)
        .select("loan_id, amount, interest_amount, principal_amount, is_settlement, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as PaymentRow[];
    },
  });

  const paymentsByLoan = new Map<string, PaymentRow[]>();
  for (const p of payments) {
    if (!p.loan_id) continue;
    const arr = paymentsByLoan.get(p.loan_id) ?? [];
    arr.push(p);
    paymentsByLoan.set(p.loan_id, arr);
  }

  const findPayment = (row: AuditRow): PaymentRow | null => {
    if (!row.loan_id) return null;
    const arr = paymentsByLoan.get(row.loan_id);
    if (!arr || arr.length === 0) return null;
    const t = new Date(row.created_at).getTime();
    let best: PaymentRow | null = null;
    let bestDelta = Infinity;
    for (const p of arr) {
      const delta = Math.abs(new Date(p.created_at).getTime() - t);
      if (delta <= 10_000 && delta < bestDelta) {
        best = p;
        bestDelta = delta;
      }
    }
    return best;
  };

  const paymentInfoFor = (row: AuditRow): PaymentInfo | null => {
    const p = findPayment(row);
    if (!p) return null;
    if (p.is_settlement === true) {
      const v = Number(p.amount);
      if (!isFinite(v)) return null;
      return { kind: "settlement", value: v };
    }
    const interestType = row.new_data?.interest_type;
    if (interestType === "fixed") {
      const installments = Number(row.new_data?.installments);
      const principal = Number(p.principal_amount);
      if (!installments || !isFinite(installments) || installments <= 0) return null;
      if (!isFinite(principal)) return null;
      return { kind: "installment", value: principal / installments };
    }
    const interest = Number(p.interest_amount);
    if (!isFinite(interest) || interest === 0) return { kind: "interest_zero" };
    return { kind: "interest", value: interest };
  };

  // Filtrar registros sem campos visíveis (backfill/ruído)
  const visible = data.filter((row) => {
    if (row.action !== "updated") return true;
    const fields = (row.changed_fields ?? []).filter((f) => !HIDDEN_FIELDS.has(f));
    return fields.length > 0;
  });

  const filtered = visible.filter((row) => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!(row.client_name ?? "").toLowerCase().includes(q)) return false;
    }
    if (filter === "all") return true;
    if (filter === "created") return row.action === "created";
    if (filter === "deleted") return row.action === "deleted" || row.action === "client_deleted";
    if (filter === "paid") {
      return row.action === "updated" && isQuitadoStatus(row.new_data?.status);
    }
    if (filter === "interest_only") {
      const info = paymentInfoFor(row);
      return !!info && (info.kind === "interest" || info.kind === "interest_zero");
    }
    if (filter === "paid_settlement") {
      const info = paymentInfoFor(row);
      return !!info && info.kind === "settlement";
    }
    if (filter === "updated") {
      if (row.action !== "updated") return false;
      // exclui quitações já cobertas em "Quitados"
      return !isQuitadoStatus(row.new_data?.status);
    }
    return true;
  });

  // Agrupar por dia
  const groups: { label: string; rows: AuditRow[] }[] = [];
  const seen = new Map<string, number>();
  for (const row of filtered) {
    const label = dayGroupLabel(row.created_at);
    const idx = seen.get(label);
    if (idx === undefined) {
      seen.set(label, groups.length);
      groups.push({ label, rows: [row] });
    } else {
      groups[idx].rows.push(row);
    }
  }

  const filterBtn = (kind: FilterKind, label: string) => (
    <Button
      key={kind}
      variant={filter === kind ? "default" : "outline"}
      size="sm"
      onClick={() => setFilter(kind)}
    >
      {label}
    </Button>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="h-5 w-5 text-primary" />
          Histórico de Alterações
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-wrap gap-2">
            {filterBtn("all", "Todos")}
            {filterBtn("created", "Criados")}
            {filterBtn("updated", "Alterados")}
            {filterBtn("paid", "Quitados")}
            {filterBtn("deleted", "Excluídos")}
            {filterBtn("interest_only", "Somente juros")}
            {filterBtn("paid_settlement", "Quitados por pagamento")}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Nenhuma alteração registrada ainda.
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((g) => (
              <div key={g.label} className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
                  {g.label}
                </div>
                <div className="space-y-3">
                  {g.rows.map((row) => {
                    const title = eventTitle(row);
                    const summary = renderSummary(row);
                    const payInfo = paymentInfoFor(row);
                    return (
                      <div
                        key={row.id}
                        className="rounded-xl border border-border/60 bg-card/40 p-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {actionBadge(row.action)}
                            <span className="font-medium text-sm truncate">
                              {row.client_name || "Cliente removido"}
                            </span>
                          </div>
                          <div className={"text-sm font-semibold " + (row.action === "client_deleted" ? "text-destructive" : "text-foreground")}>
                            {title}
                          </div>
                          {payInfo && (
                            <div className="text-sm mt-1">
                              {payInfo.kind === "settlement" && (
                                <span className="font-semibold text-success">
                                  Quitado · {fmtBRL(payInfo.value)}
                                </span>
                              )}
                              {payInfo.kind === "interest" && (
                                <span className="text-warning">
                                  Somente juros · {fmtBRL(payInfo.value)}
                                </span>
                              )}
                              {payInfo.kind === "interest_zero" && (
                                <span className="text-warning">Renovado sem juros</span>
                              )}
                              {payInfo.kind === "installment" && (
                                <span className="text-primary">
                                  Parcela paga · {fmtBRL(payInfo.value)}
                                </span>
                              )}
                            </div>
                          )}
                          {summary && <div className="text-sm mt-1">{summary}</div>}
                        </div>
                        <div className="text-xs text-muted-foreground shrink-0 sm:text-right">
                          {fmtTimeBR(row.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {data.length >= limit && (
              <div className="pt-2 flex justify-center">
                <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 300)}>
                  Carregar mais
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LoanAuditLog;